import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { getOrder, updateOrder } from '@/api/orders';
import { toast } from 'sonner';

const principal = binding => binding?.user?.id && binding?.user?.tenant_id
  && ['cloud', 'offline'].includes(binding.source)
  ? JSON.stringify([binding.user.tenant_id, binding.user.id, binding.source]) : null;
const noteText = value => typeof value === 'string' ? value : '';

// TCA-26101406: mounted order openings own drafts, credentials and continuations.
// Retiring a UI owner does not cancel or undo an already-issued server write.
export function useDesignerNote(order, { offline = false, successToast = false, onNoteSaved } = {}) {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const [, render] = useState(0);
  const cycle = useMemo(() => ({
    orderId: order?.id, principal: principal(sessionBinding), revision: sessionBinding?.revision,
    source: sessionBinding?.source, value: noteText(order?.designer_notes),
    baseline: noteText(order?.designer_notes), incoming: noteText(order?.designer_notes),
    active: false, busy: false, status: 'idle', message: '', controller: null,
  // A replacement login cannot adopt an already-open draft. A new order/remount
  // is the boundary that admits new authority, matching the order-editor policy.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [order?.id]);
  const latest = useRef(null);
  latest.current = { cycle, sessionBinding, getToken, offline, successToast, onNoteSaved };
  const visible = !!cycle.orderId && !!cycle.principal && cycle.revision === revision
    && sessionBinding?.revision === cycle.revision && principal(sessionBinding) === cycle.principal;
  const owns = () => cycle.active && latest.current.cycle === cycle
    && !!cycle.orderId && !!cycle.principal && getActiveSessionRevision() === cycle.revision
    && latest.current.sessionBinding?.revision === cycle.revision
    && principal(latest.current.sessionBinding) === cycle.principal;
  const online = () => owns() && cycle.source === 'cloud' && !latest.current.offline && !isDesktopOffline();
  const publish = () => { if (owns()) render(value => value + 1); };
  const publishConfirmedNote = text => {
    if (!owns()) return;
    try { latest.current.onNoteSaved?.(cycle.orderId, text, owns); }
    catch {
      // Display synchronization cannot turn an acknowledged write into a retry.
      cycle.message = `${cycle.message || 'The note was saved.'} The shared display could not update; keep any unfinished draft before refreshing.`;
    }
  };
  useLayoutEffect(() => {
    cycle.active = true;
    return () => { cycle.active = false; cycle.controller?.abort(); };
  }, [cycle]);
  useLayoutEffect(() => {
    if (!visible) cycle.controller?.abort();
  }, [cycle, visible]);
  useLayoutEffect(() => {
    const incoming = noteText(order?.designer_notes);
    if (!owns() || incoming === cycle.incoming) return;
    cycle.incoming = incoming;
    // A background refresh is not a recovery check and cannot dismiss failure
    // state or overwrite an unfinished draft, even if its text happens to match.
    if (!cycle.busy && ['idle', 'verified'].includes(cycle.status) && cycle.value === cycle.baseline) {
      cycle.value = incoming;
      cycle.baseline = incoming;
      cycle.status = 'idle';
      cycle.message = '';
      publish();
    }
  // Callback identities are not incoming-order changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle, order?.designer_notes, visible]);

  const setValue = value => {
    if (!online() || cycle.busy) return;
    cycle.value = noteText(value);
    if (cycle.status === 'verified') { cycle.status = 'idle'; cycle.message = ''; }
    publish();
  };
  const begin = status => {
    if (!online() || cycle.busy) return null;
    const token = latest.current.getToken?.();
    if (!token || !online()) {
      if (owns()) { cycle.message = 'Sign in again before saving or checking this note.'; publish(); }
      return null;
    }
    cycle.busy = true;
    cycle.status = status;
    cycle.message = status === 'saving' ? 'Saving note…' : 'Checking saved note…';
    publish();
    return { headers: { Authorization: `Bearer ${token}` }, timeout: 10000, suppressServerErrorNotification: true };
  };
  const write = async explicit => {
    if (!online() || cycle.busy) return false;
    const allowed = explicit ? ['rejected', 'mismatch'] : ['idle', 'verified'];
    if (!allowed.includes(cycle.status) || (!explicit && cycle.value === cycle.baseline)) return false;
    const request = begin('saving');
    if (!request) return false;
    const submitted = cycle.value;
    const notifySuccess = latest.current.successToast;
    try {
      try { await updateOrder(cycle.orderId, { designer_notes: submitted }, request); }
      catch (error) {
        if (!owns()) return false;
        const status = error?.response?.status;
        const rejected = status >= 400 && status < 500 && status !== 408;
        cycle.status = rejected ? 'rejected' : 'unknown';
        const detail = error?.response?.data?.detail;
        cycle.message = rejected
          ? `${typeof detail === 'string' ? detail : 'The note was not saved.'} Your draft is retained. Retry saving when ready.`
          : 'The save result is not confirmed; it may have succeeded. Your draft is retained. Check the saved note before retrying.';
        return false;
      }
      if (!owns()) return false;
      cycle.baseline = submitted;
      cycle.status = 'idle';
      cycle.message = '';
      publishConfirmedNote(submitted);
      // Feedback failures never relabel an acknowledged write as rejected.
      if (notifySuccess) { try { toast.success('Note saved'); } catch { /* Saved remains saved. */ } }
      return true;
    } finally {
      cycle.busy = false;
      publish();
    }
  };
  const checkSavedNote = async () => {
    if (!['unknown', 'mismatch'].includes(cycle.status)) return false;
    const request = begin('checking');
    if (!request) return false;
    const controller = new AbortController();
    cycle.controller = controller;
    try {
      const saved = await getOrder(cycle.orderId, { ...request, signal: controller.signal });
      if (!owns()) return false;
      if (saved?.id !== cycle.orderId || !Object.prototype.hasOwnProperty.call(saved, 'designer_notes')
        || (saved.designer_notes !== null && typeof saved.designer_notes !== 'string')) throw new Error('Invalid note readback');
      const text = noteText(saved.designer_notes);
      cycle.baseline = text;
      if (text === cycle.value) {
        cycle.status = 'verified';
        cycle.message = 'The saved note matches your draft.';
      } else {
        cycle.status = 'mismatch';
        cycle.message = 'The saved note differs from your draft. Your draft is retained. The earlier save may still finish; retry saving only if you want to send this draft again.';
      }
      publishConfirmedNote(text);
      return true;
    } catch {
      if (owns()) {
        cycle.status = 'unknown';
        cycle.message = 'The saved note could not be checked. Your draft is retained. Check again before retrying the save.';
      }
      return false;
    } finally {
      cycle.controller = null;
      cycle.busy = false;
      publish();
    }
  };
  return {
    value: visible ? cycle.value : '', setValue, save: () => write(false), retrySave: () => write(true), checkSavedNote,
    saving: visible && cycle.status === 'saving', checking: visible && cycle.status === 'checking',
    readOnly: !visible || cycle.busy || offline || isDesktopOffline() || cycle.source !== 'cloud',
    status: visible ? cycle.status : 'retired', message: visible ? cycle.message : '', visible,
    canRetry: visible && !cycle.busy && ['rejected', 'mismatch'].includes(cycle.status),
    needsCheck: visible && !cycle.busy && ['unknown', 'mismatch'].includes(cycle.status),
  };
}
